/* eslint-disable @typescript-eslint/no-explicit-any */
import {Grid, FormHelperText, TextFieldProps} from '@mui/material';
import {DateRangePickerProps} from 'components/DateRangePicker/DateRangePicker';
import DropDownInput from 'components/DropDownInput';
import {DropDownItemProps} from 'components/DropDownInput/DropDownInput';
import OutlinedInput from 'components/OutlinedInput';
import UploadPicker from 'components/UploadPicker';
import HolidaysDatePickerController from 'controllers/HolidaysDatePickerController';
import {FC, useEffect} from 'react';
import {FieldValues, RegisterOptions, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import LoaderWithBackdrop from 'components/LoaderWithBackdrop';
import FormLabel from 'components/FormLabel/FormLabel';
import './FormController.scss';
import SearchBar, {SearchBarProps} from '../../components/SearchBar/SearchBar';
import Checkbox from 'components/Checkbox';
import {CheckboxProps} from 'components/Checkbox/Checkbox';
import LeaveDateRangePickerController from 'controllers/LeaveDateRangePickerController';
import {
  FormRangeDateWithHalfDay,
  LeaveDateRangePickerProps,
} from 'controllers/LeaveDateRangePickerController/LeaveDateRangePickerController';
import TextAreaInput, {
  TextAreaInputProps,
} from 'components/textArea/TextAreaInput';

interface FormControllerChildProps {
  onSubmit: Function;
  reset: any;
}

export type ControlType =
  | 'file'
  | 'date'
  | 'select'
  | 'label'
  | 'text'
  | 'search'
  | 'checkbox'
  | 'textArea'
  | 'leaveDateRangePicker';

interface ControlTestProps {
  'data-testid': string;
}

/** Props specific to file-type controls, forwarded directly to UploadPicker. */
interface FileControlProps {
  label?: string;
  title?: string;
  className?: string;
  allowedMimeTypes?: string[];
  maxFileSizeBytes?: number;
}

export interface FormControllerControlProps {
  name: string;
  type?: ControlType;
  props?:
    | ControlTestProps
    | TextFieldProps
    | DropDownItemProps
    | DateRangePickerProps
    | SearchBarProps
    | CheckboxProps
    | TextAreaInputProps
    | LeaveDateRangePickerProps
    | FileControlProps;
  options?: RegisterOptions;
}

export interface FormControllerProps {
  controls: Array<FormControllerControlProps | FormControllerControlProps[]>;
  children: (props: FormControllerChildProps) => JSX.Element;
  itemProps?: any;
  onSubmit: Function;
  className?: string;
  watch?: (data: any) => void;
  isLoading?: boolean;
  defaultValues?: FieldValues;
}

const FormController: FC<FormControllerProps> = props => {
  const {controls, children, itemProps, isLoading, defaultValues} = props;
  const {t} = useTranslation();

  const {
    watch,
    register,
    handleSubmit,
    formState: {errors},
    reset,
    setValue,
  } = useForm({reValidateMode: 'onChange'});

  useEffect(() => {
    if (defaultValues) {
      Object.keys(defaultValues).forEach(key => {
        setValue(key, defaultValues[key]);
      });
    }
  }, [defaultValues, setValue]);

  watch(values => {
    props.watch && props.watch(values);
  });

  const gridContainerProps = {
    spacing: 2,
  };

  const onSubmit = (args: Map<string, any>) =>
    handleSubmit((values: any) => {
      props.onSubmit(values, args);
    });

  const getComponentType = (control: FormControllerControlProps) => {
    switch (control.type) {
      case 'file':
        // Register the field at render time so RHF enforces validation rules
        // (including required) when handleSubmit is called, even if the user
        // never interacts with the field
        register(control.name, control.options);
        return {
          component: UploadPicker,
          defaultProps: {
            onSelectFiles: (files: File[]) => {
              register(control.name, control.options).onChange({
                target: {
                  name: control.name,
                  // Store the single selected File (or undefined when cleared)
                  value: files.length > 0 ? files[0] : undefined,
                },
              });
            },
            key: 1,
            // Title shown above the card; falls back to label if no explicit title set
            title:
              (control.props as any)?.title ||
              (control.props as DropDownItemProps)?.label,
            // Generic placeholder text shown inside the card
            label: t('leaves.attach_label'),
            // Show red asterisk on the title to signal the field is mandatory
            required: !!control.options?.required,
            // Drive red-border + helper-text when RHF marks the field as invalid after submit
            hasError: !!errors[control.name],
            // Forward optional file constraints declared by the parent control
            allowedMimeTypes: (control.props as any)?.allowedMimeTypes,
            maxFileSizeBytes: (control.props as any)?.maxFileSizeBytes,
          },
        };
      case 'date':
        return {
          component: HolidaysDatePickerController,
          defaultProps: {
            onChangeDates: (startDate: Date | null, endDate: Date | null) => {
              register(control.name, control.options).onChange({
                target: {
                  name: control.name,
                  value: {startDate, endDate},
                },
              });
            },
            defaultValue: defaultValues?.[control.name],
            error: !!errors[control.name],
            helperText: errors[control.name]?.message as string | undefined,
          },
        };
      case 'select':
        return {
          component: DropDownInput,
          defaultProps: {
            ...register(control.name, control.options),
            // Use '' as fallback so MUI Select never receives undefined as its initial value
            defaultValue: defaultValues?.[control.name] ?? '',
            error: !!errors[control.name],
            helperText: errors[control.name]?.message as string | undefined,
          },
        };
      case 'label':
        return {
          component: FormLabel,
        };
      case 'search':
        return {
          component: SearchBar,
          defaultProps: {
            onChange: (text: string) => {
              register(control.name, control.options).onChange({
                target: {
                  name: control.name,
                  value: text,
                },
              });
            },
          },
        };
      case 'checkbox':
        return {
          component: Checkbox,
          defaultProps: {
            CheckboxProps: {
              ...register(control.name, control.options),
            },
          },
        };
      case 'leaveDateRangePicker':
        return {
          component: LeaveDateRangePickerController,
          defaultProps: {
            onChange: (leaveRangeDate: FormRangeDateWithHalfDay) => {
              register(control.name, control.options).onChange({
                target: {
                  name: control.name,
                  value: leaveRangeDate,
                },
              });
            },
            defaultValue: defaultValues?.[control.name],
          },
        };
      case 'textArea':
        return {
          component: TextAreaInput,
          defaultProps: {
            inputDefaults: {
              ...register(control.name, control.options),
              ...(control.props as TextFieldProps)?.InputProps,
            },
            error: !!errors[control.name],
            className: 'w-full',
          },
        };
      default:
        return {
          component: OutlinedInput,
          defaultProps: {
            InputProps: {
              ...register(control.name, control.options),
              ...(control.props as TextFieldProps)?.InputProps,
            },
            error: !!errors[control.name],
            helperText: errors[control.name]?.message,
            className: 'w-full',
          },
        };
    }
  };

  const getControl = (control: FormControllerControlProps) => {
    const controlType = getComponentType(control);
    const Control: any = controlType.component;
    const controlProps = {
      ...control.props,
      ...controlType.defaultProps,
    };
    return <Control {...controlProps} />;
  };

  const getFormGrid = (
    item: FormControllerControlProps | FormControllerControlProps[],
    index: number,
    isRow?: boolean
  ) => {
    if (Array.isArray(item)) {
      return (
        <Grid container {...gridContainerProps} key={index}>
          {item.map((control, subIndex) =>
            getFormGrid(control, subIndex, true)
          )}
        </Grid>
      );
    } else if (isRow) {
      const dateErrorMsg =
        item.type === 'date'
          ? (errors[item.name]?.message as string | undefined)
          : undefined;
      return (
        <Grid item key={index}>
          {getControl(item)}
          {dateErrorMsg && (
            <FormHelperText error>{dateErrorMsg}</FormHelperText>
          )}
        </Grid>
      );
    }
    const dateErrorMsg =
      item.type === 'date'
        ? (errors[item.name]?.message as string | undefined)
        : undefined;
    return (
      <Grid key={index} item xs={6} {...itemProps}>
        {getControl(item)}
        {dateErrorMsg && <FormHelperText error>{dateErrorMsg}</FormHelperText>}
      </Grid>
    );
  };

  const renderFormControls = () =>
    controls.map((control, controlIndex) => getFormGrid(control, controlIndex));

  return (
    <form
      className={`FormController ${props.className}`}
      data-testid="FormController"
    >
      <Grid container {...gridContainerProps}>
        {renderFormControls()}
      </Grid>
      <div className="actions flex justify-end">
        {children({
          onSubmit,
          reset,
        })}
      </div>
      <LoaderWithBackdrop isLoading={!!isLoading} />
    </form>
  );
};

export default FormController;
